import type { EventData, Venue } from "@evnt/schema";
import type { EditAtom } from "../../edit-atom";
import { Button, Center, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useMemo, type ReactNode } from "react";
import { EditVenue } from "./EditVenue";
import { focusAtom } from "jotai-optics";

export const EditVenuesList = ({
	data,
	filter,
	onAddUpdate,
	addLabel = "Add Venue",
	buttons,
}: {
	data: EditAtom<EventData>,
	filter?: (venue: Venue, data: EventData) => boolean;
	onAddUpdate?: (v: EventData, newVenue: Venue) => EventData;
	addLabel?: ReactNode;
	buttons?: ReactNode;
}) => {
	const lengthAtom = useMemo(() => atom((get) => get(data).venues?.length ?? 0), [data]);
	const length = useAtomValue(lengthAtom);

	const addVenue = useSetAtom(useMemo(() => atom(null, (get, set) => {
		let id = get(data).venues?.length ?? 0;
		const existingVenueIds = new Set(get(data).venues?.map(v => v.id));
		while (existingVenueIds.has(id.toString())) id++;

		const newVenue: Venue = {
			id: id.toString(),
			$type: "directory.evnt.venue.unknown",
			name: {},
		};

		set(data, (prev) => {
			let next: EventData = {
				...prev,
				venues: [...(prev.venues ?? []), newVenue],
			};
			if (onAddUpdate) next = onAddUpdate(next, newVenue);
			return next;
		});
	}), [data, onAddUpdate]));

	const children = new Array(length).fill(null).map((_, i) => (
		<EditVenue
			key={i}
			data={data}
			venue={focusAtom(data, o => o.prop("venues").valueOr([]).at(i)) as EditAtom<Venue>}
		/>
	));

	return (
		<Stack gap={4}>
			<Group gap={4} justify="space-between">
				<Title order={4}>
					Venues ({length})
				</Title>
				<Button
					onClick={addVenue}
				>
					{addLabel}
				</Button>
			</Group>
			{length === 0 && (
				<Paper bg="dark" p="md" py="xl" ta="center">
					<Stack h="100%" align="center" justify="center">
						<Text c="dimmed">
							No venues added yet!
						</Text>
						<Text c="dimmed" fz="xs">
							Venues define where the event takes place; physical or online. You can add multiple venues for different locations.
						</Text>
					</Stack>
				</Paper>
			)}
			<Stack>
				{children}
			</Stack>
		</Stack>
	);
};
